import { useNuxtApp } from 'nuxt/app';
import type { ApiError, ApiInstance } from '~/types/plugins/api';

const CONNECTION_ERROR =
  'Unable to connect. Please check your internet connection.';
const SERVER_ERROR = 'Server error. Please try again later.';
const GENERIC_ERROR = 'Something went wrong. Please try again.';

export type ReportProblemContext = {
  reportType: string;
  reportId: string;
} | null;

/**
 * Both `/report` and `/issues` accept `attachments[]` as an unbounded array;
 * the 3-image cap is a product decision, so it lives here rather than being
 * inferred from the API.
 */
export const MAX_ATTACHMENTS = 3;

/**
 * Submission state for the "Help us improve this information" / "Help Us
 * Improve" form.
 *
 * When `context` identifies the specific entity being reported (candidate,
 * party, or campaign — passed via the report-problem link's query params),
 * this POSTs to `/report` so the report is linked to that entity by its
 * public slug. Otherwise (reached generically, e.g. from Support) it POSTs
 * to `/issues` as a general Help Centre issue. Both endpoints allow guests.
 */
export function useReportProblem(context: ReportProblemContext) {
  const { $apiClient } = useNuxtApp() as unknown as { $apiClient: ApiInstance };

  const subject = ref('');
  const description = ref('');
  const attachments = ref<File[]>([]);
  const pending = ref(false);
  const isSuccess = ref(false);
  const isError = ref(false);
  const errorMessage = ref('');

  const resolveErrorMessage = (err: ApiError): string => {
    const status = err?.status;

    // status 0 / undefined is set by the API client for network & timeout errors
    if (!status) return CONNECTION_ERROR;
    if (status >= 500) return SERVER_ERROR;
    return err?.message || GENERIC_ERROR;
  };

  const buildFormData = (): FormData => {
    const formData = new FormData();
    formData.append('subject', subject.value.trim());
    formData.append('description', description.value.trim());
    attachments.value.slice(0, MAX_ATTACHMENTS).forEach((file) => {
      formData.append('attachments[]', file);
    });
    if (context) {
      formData.append('report_type', context.reportType);
      formData.append('report_id', context.reportId);
    }
    return formData;
  };

  const submit = async (): Promise<boolean> => {
    if (pending.value) return false;

    pending.value = true;
    isError.value = false;
    errorMessage.value = '';

    try {
      const endpoint = context ? '/report' : '/issues';
      await $apiClient.post(endpoint, buildFormData());

      isSuccess.value = true;
      return true;
    } catch (err) {
      isError.value = true;
      errorMessage.value = resolveErrorMessage(err as ApiError);
      return false;
    } finally {
      pending.value = false;
    }
  };

  return {
    subject,
    description,
    attachments,
    pending,
    isSuccess,
    isError,
    errorMessage,
    submit,
  };
}
